/**
 * Ground hazards overlay — prisons and military zones from OpenStreetMap.
 * Uses filtered prison-military.json (~1 MB) instead of full hazards.json (7.6 MB).
 *
 * Data source: public/data/prison-military.json
 */
import mapboxgl from 'mapbox-gl';
import { fetchJSON } from '../utils/fetch-data';

const SOURCE_ID     = 'hazards';
const LYR_MIL_FILL  = 'hazard-mil-fill';
const LYR_MIL_LINE  = 'hazard-mil-border';
const LYR_MIL_PTS   = 'hazard-mil-points';
const LYR_PRIS_FILL = 'hazard-prison-fill';
const LYR_PRIS_LINE = 'hazard-prison-border';
const LYR_PRIS_PTS  = 'hazard-prison-points';
const LYR_GOV_PTS   = 'hazard-gov-points';
const ALL_LAYERS = [LYR_MIL_FILL, LYR_MIL_LINE, LYR_MIL_PTS, LYR_PRIS_FILL, LYR_PRIS_LINE, LYR_PRIS_PTS, LYR_GOV_PTS];

let geojsonData: GeoJSON.FeatureCollection | null = null;
let activePopup: mapboxgl.Popup | null = null;

async function loadData(): Promise<GeoJSON.FeatureCollection> {
  if (geojsonData) return geojsonData;
  geojsonData = await fetchJSON<GeoJSON.FeatureCollection>('/data/prison-military.json');
  return geojsonData;
}

export async function addHazardLayers(map: mapboxgl.Map): Promise<void> {
  const data = await loadData();
  if (map.getSource(SOURCE_ID)) return;

  map.addSource(SOURCE_ID, { type: 'geojson', data });

  const beforeAirspace = map.getLayer('airspace-zones') ? 'airspace-zones' : undefined;

  // Military zones — polygons
  map.addLayer({
    id: LYR_MIL_FILL, type: 'fill', source: SOURCE_ID, minzoom: 4,
    filter: ['all', ['==', ['get', 'category'], 'military'], ['==', ['geometry-type'], 'Polygon']],
    paint: { 'fill-color': 'rgba(239, 68, 68, 0.15)', 'fill-opacity': 1 },
  }, beforeAirspace);

  map.addLayer({
    id: LYR_MIL_LINE, type: 'line', source: SOURCE_ID, minzoom: 4,
    filter: ['all', ['==', ['get', 'category'], 'military'], ['==', ['geometry-type'], 'Polygon']],
    paint: {
      'line-color': '#ef4444', 'line-opacity': 0.75,
      'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.8, 10, 1.6] as any,
    },
  }, beforeAirspace);

  // Military zones — points
  map.addLayer({
    id: LYR_MIL_PTS, type: 'circle', source: SOURCE_ID, minzoom: 8,
    filter: ['all', ['==', ['get', 'category'], 'military'], ['==', ['geometry-type'], 'Point']],
    paint: {
      'circle-color': '#ef4444', 'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 3, 12, 6] as any,
      'circle-stroke-color': 'rgba(255,255,255,0.3)', 'circle-stroke-width': 1, 'circle-opacity': 0.85,
    },
  });

  // Prisons — polygons
  map.addLayer({
    id: LYR_PRIS_FILL, type: 'fill', source: SOURCE_ID, minzoom: 4,
    filter: ['all', ['==', ['get', 'category'], 'prison'], ['==', ['geometry-type'], 'Polygon']],
    paint: { 'fill-color': 'rgba(168, 85, 247, 0.15)', 'fill-opacity': 1 },
  }, beforeAirspace);

  map.addLayer({
    id: LYR_PRIS_LINE, type: 'line', source: SOURCE_ID, minzoom: 4,
    filter: ['all', ['==', ['get', 'category'], 'prison'], ['==', ['geometry-type'], 'Polygon']],
    paint: {
      'line-color': '#a855f7', 'line-opacity': 0.75,
      'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.8, 10, 1.6] as any,
      'line-dasharray': [4, 3],
    },
  }, beforeAirspace);

  // Prisons — points
  map.addLayer({
    id: LYR_PRIS_PTS, type: 'circle', source: SOURCE_ID, minzoom: 8,
    filter: ['all', ['==', ['get', 'category'], 'prison'], ['==', ['geometry-type'], 'Point']],
    paint: {
      'circle-color': '#a855f7', 'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 3, 12, 6] as any,
      'circle-stroke-color': 'rgba(255,255,255,0.3)', 'circle-stroke-width': 1, 'circle-opacity': 0.85,
    },
  });

  // Government buildings — points (indigo circles) — courthouses excluded
  map.addLayer({
    id: LYR_GOV_PTS, type: 'circle', source: SOURCE_ID, minzoom: 9,
    filter: ['all', ['==', ['get', 'category'], 'government'], ['==', ['geometry-type'], 'Point'], ['!=', ['get', 'subtype'], 'courthouse']],
    paint: {
      'circle-color': '#6366f1', 'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 2, 14, 6] as any,
      'circle-stroke-color': 'rgba(255,255,255,0.3)', 'circle-stroke-width': 1, 'circle-opacity': 0.85,
    },
  });

  // Event handlers
  for (const lyr of ALL_LAYERS) {
    map.on('click', lyr, onHazardClick);
    map.on('mouseenter', lyr, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', lyr, () => { map.getCanvas().style.cursor = ''; });
  }
}

export function removeHazardLayers(map: mapboxgl.Map): void {
  activePopup?.remove();
  activePopup = null;

  for (const lyr of ALL_LAYERS) {
    if (map.getLayer(lyr)) {
      map.off('click', lyr, onHazardClick);
      map.removeLayer(lyr);
    }
  }
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}

const SUBTYPE_LABELS: Record<string, string> = {
  parliament:  'Parliament / Legislature',
  courthouse:  'Courthouse / Court',
  capitol:     'Capitol Building',
  government:  'Government Building',
};

const CATEGORY_META: Record<string, { label: string; color: string; icon: string }> = {
  military:   { label: 'Military Zone',                  color: '#ef4444', icon: '⚠️' },
  prison:     { label: 'Prison / Correctional Facility', color: '#a855f7', icon: '🔒' },
  government: { label: 'Government Building',            color: '#6366f1', icon: '🏛️' },
};

function onHazardClick(e: mapboxgl.MapLayerMouseEvent) {
  if (!e.features?.length) return;
  e.originalEvent.stopPropagation();

  const props = e.features[0].properties as Record<string, string>;
  const meta = CATEGORY_META[props.category] ?? { label: props.category, color: '#94a3b8', icon: '📍' };

  // For government buildings, show the subtype label if available
  const displayLabel = props.category === 'government' && props.subtype
    ? (SUBTYPE_LABELS[props.subtype] ?? meta.label)
    : meta.label;

  activePopup?.remove();
  activePopup = new mapboxgl.Popup({ closeButton: true, maxWidth: '240px' })
    .setLngLat(e.lngLat)
    .setHTML(`
      <div class="hz-popup">
        <div class="hz-popup-type" style="color:${meta.color}">
          <span class="hz-icon">${meta.icon}</span> ${displayLabel}
        </div>
        ${props.name ? `<div class="hz-popup-name">${props.name}</div>` : ''}
        <div class="hz-popup-note">Drone operators must check local regulations before flying near this area.</div>
        <div class="hz-popup-note" style="margin-top:6px;opacity:0.75;font-size:11px;">Source: OpenStreetMap, indicative only, not an official no-fly list.</div>
      </div>
    `)
    .addTo(e.target as mapboxgl.Map);
}
